import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAllFeatureFlags } from 'hooks/useAllFeatureFlags'
import useScanSummarySettings from 'components/Support/SupportDashboard/EditDoctor/useScanSummarySettings'
import useGaFlags from './useGaFlags'
import usePracticeConfiguration from './usePracticeConfiguration'

export default (user, practiceOwnerUser = null) => {
  if (user?.type === 'patient') {
    return useMemo(() => JSON.parse(user?.featureFlags?.flags || '{}'), [user.featureFlags.flags])
  }

  const { doctorFeatureFlags } = useAllFeatureFlags()
  const { scanSummarySettings } = usePracticeConfiguration()
  const { isScanSummaryVersionAtLeast } = useScanSummarySettings(scanSummarySettings)
  const gaFlags = useGaFlags()
  const mpPracticeFFs = useSelector(state => state.multiPracticeReducer.practiceFFs)

  const accountOwnerUser = useSelector(state => state.practiceReducer?.accountOwner?.user)

  const accountOwnerFF = useMemo(() => {
    const currentUser = practiceOwnerUser || (!user && accountOwnerUser)
    return JSON.parse(currentUser?.featureFlags?.flags || '{}')
  }, [accountOwnerUser, practiceOwnerUser, user])

  const currentDoctorFeatureFlags = useSelector(state => state.profileReducer?.doctor?.user?.featureFlags)

  const practiceFFs = useMemo(() => doctorFeatureFlags.filter(flag => flag?.isPractice), [doctorFeatureFlags])
  const sharedFF = useMemo(
    () =>
      Object.keys(accountOwnerFF).reduce((currFFs, currFFKey) => {
        if (practiceFFs.find(currPracticeFF => currPracticeFF.key === currFFKey && currPracticeFF.type === 'doctor')) {
          return {
            ...currFFs,
            [currFFKey]: accountOwnerFF[currFFKey]
          }
        }

        return currFFs
      }, {}),
    [accountOwnerFF, practiceFFs]
  )

  const appSettingsFF = useMemo(() => {
    return { recordsCropSettings: isScanSummaryVersionAtLeast('V2-1-0') }
  }, [isScanSummaryVersionAtLeast])

  const scanReviewVideoBitrate = useMemo(() => {
    const webSpecificBitrate = gaFlags?.scanReviewWebVideoBitrate
    const sharedBitrate = gaFlags?.videoBitrate

    if (typeof webSpecificBitrate === 'number') {
      return webSpecificBitrate
    }

    if (typeof sharedBitrate === 'number') {
      return sharedBitrate
    }

    return undefined
  }, [gaFlags])

  return useMemo(() => {
    let featureFlags
    if (!user) {
      featureFlags = currentDoctorFeatureFlags
    } else {
      featureFlags = user?.featureFlags
    }

    const result = {
      ...(featureFlags ? JSON.parse(featureFlags.flags) : {}),
      ...sharedFF,
      ...(user ? {} : mpPracticeFFs),
      ...appSettingsFF,
      videoBitrate: scanReviewVideoBitrate,
      scanReviewVideoBitrate
    }

    return result
  }, [currentDoctorFeatureFlags, mpPracticeFFs, sharedFF, user, appSettingsFF, scanReviewVideoBitrate])
}
